import React, { useState, useMemo } from 'react';
import { Grid, Typography, useTheme } from '@mui/material';
import { Svgs } from '../../../assets/svg';
import { Spacer } from '../../../components';
import { AdminTierTypeEnum } from '../../../services/drillDown';
import {
  sortReportsAlphabetically,
  getNestedReports,
} from '../../../data/report/helpers/userAccountsUtils';
import BreakdownTable from './BreakdownTable';
import { Loading } from '../../../hooks/useLoading';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { BreakdownReport } from '../../../models/ReportData';

interface ReportBreakdownProps {
  tier: AdminTierTypeEnum | undefined;
  breakdownReport: BreakdownReport;
  isEmpty: boolean;
}

const ReportBreakdown: React.FC<ReportBreakdownProps> = ({ tier, breakdownReport, isEmpty }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const hasBreakdown = tier === AdminTierTypeEnum.Team || tier === AdminTierTypeEnum.Organization;

  const pricebookReports = useMemo(() => getNestedReports(breakdownReport), [breakdownReport]);
  const sortedReports = useMemo(
    () => sortReportsAlphabetically(pricebookReports),
    [pricebookReports],
  );

  const {
    displayedItems: displayedReports,
    hasMore,
    lastElementRef,
  } = useInfiniteScroll(sortedReports, expanded);

  if (isEmpty || !hasBreakdown) {
    return null;
  }

  return (
    hasBreakdown && (
      <Grid container direction='column'>
        <Grid display='flex' alignItems='center' mb={3} onClick={() => setExpanded(prev => !prev)}>
          {expanded ? (
            <Svgs.IconCollapse fill={theme.palette.info.main} />
          ) : (
            <Svgs.IconAdd fill={theme.palette.info.main} />
          )}
          <Spacer width='xxs' />
          <Typography variant='p18SemiBold' color='secondary.main'>
            {expanded ? 'Hide' : 'See'} breakdown by {tier?.toLowerCase()}
          </Typography>
          <Spacer width='xxs' />
          {tier === AdminTierTypeEnum.Team ? (
            <Svgs.IconTeamsDefault />
          ) : (
            <Svgs.IconOrganizationsDefault />
          )}
        </Grid>

        {expanded && (
          <Grid>
            {displayedReports.map((pricebookReport, index) => (
              <Grid
                key={pricebookReport.id}
                ref={index === displayedReports.length - 1 ? lastElementRef : null}>
                <BreakdownTable report={pricebookReport} />
              </Grid>
            ))}

            {hasMore && <Loading />}
          </Grid>
        )}
      </Grid>
    )
  );
};

export default ReportBreakdown;
